import Styles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getFirestore, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { RxCross2 } from 'react-icons/rx';
import { FaRegCircle } from 'react-icons/fa6';
import { HiOutlineDocumentText } from 'react-icons/hi';
import CompanyDefaultImage from '../../image/default/home.png';

// 進路活動(見学・説明会参加・応募)の学校別許可制・実施ログ(careerActivityRequests)。
// jobActivityReports(就職活動報告、コーセー美容専門学校向けの紙の公欠申請の電子化)とは
// 意味も用途も別物(5.absence/index.tsxのUI/UXを強く参考にしているが、別の機能)。
//
// schools/{id}.careerActivityApproval.required=true の学校(YICビューティーモード専門学校)は、
// 学生がサロン見学・会社説明会参加・応募をするたびに学校の事前承認が必要(先生が承認するまで
// サロンに通知が届かない)。承認待ち(pending)タブはこの学校だけに出す。
// それ以外の学校は自由に行える代わりに、学校は実施ログ(completedタブ)で一覧を見られる。
// 承認必須校でも、承認済み(approved)は実施ログに合流して表示する。
//
// 学校が書き込めるのは status と decidedAt/decidedBy/rejectedReason(不許可のときだけ)だけ。
// 片道のみ(pending→approved/rejected)で、戻す機能は無い(就職活動報告と異なる点)。
// 権限は firestore.rules の match /careerActivityRequests。

const KIND_LABELS: Record<string, string> = {
  visit: 'サロン見学',
  infoSession: '会社説明会',
  apply: '応募',
};

type Status = 'pending' | 'approved' | 'rejected' | 'logged';

type CareerActivityRequest = {
  id: string;
  uid: string;
  kind: string;
  recruitId: string;
  companyName: string;
  storeName: string;
  mode: 'approval' | 'log';
  status: Status;
  studentName: string;
  requestedAt: Date | null;
  decidedAt: Date | null;
  rejectedReason: string;
};

const kindLabel = (r: CareerActivityRequest) => KIND_LABELS[r.kind] ?? r.kind;

const logoUrl = (recruitId: string) =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/companies_logo%2Fresize_images%2F${recruitId.replace(/_\d{4}$/, '')}_200x200?alt=media&token=`;

const toDate = (v: unknown): Date | null => (v instanceof Timestamp ? v.toDate() : null);
const formatShortDate = (d: Date | null) => (d ? `${d.getMonth() + 1}/${d.getDate()}` : '-');
const formatLongDate = (d: Date | null) =>
  (d ? `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日` : '-');
// 検索は大文字・小文字と全角・半角を区別しない
const normalize = (v: string) => v.normalize('NFKC').toLowerCase();

type Props = {
  schoolId: string;
  // null は読み込み中(承認待ちサブタブを出すかどうかがまだ決まっていない)
  careerActivityApprovalRequired: boolean | null;
  allUsers: any[];
  hashTags: string[];
  filteredUsers: any[];
};

const CareerActivityRequests: React.FC<Props> = ({ schoolId, careerActivityApprovalRequired, allUsers, hashTags, filteredUsers }) => {
  const [ items, setItems ] = useState<CareerActivityRequest[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState(false);
  const [ text, setText ] = useState('');
  const [ tab, setTab ] = useState<'pending' | 'completed'>('completed');
  const [ detail, setDetail ] = useState<CareerActivityRequest | null>(null);
  const [ savingId, setSavingId ] = useState('');
  const [ rejectTarget, setRejectTarget ] = useState<CareerActivityRequest | null>(null);

  const showPendingTab = careerActivityApprovalRequired === true;

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    const q = query(collection(getFirestore(), 'careerActivityRequests'), where('schoolId', '==', schoolId));
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => {
        const x = d.data();
        const status: Status = ['pending', 'approved', 'rejected', 'logged'].includes(x.status) ? x.status : 'logged';
        return {
          id: d.id,
          uid: x.uid ?? '',
          kind: x.kind ?? '',
          recruitId: x.recruitId ?? '',
          companyName: x.companyName ?? '',
          storeName: x.storeName ?? '',
          mode: x.mode === 'approval' ? 'approval' : 'log',
          status,
          studentName: x.studentName ?? '',
          requestedAt: toDate(x.requestedAt),
          decidedAt: toDate(x.decidedAt),
          rejectedReason: x.rejectedReason ?? '',
        };
      }));
      setLoading(false);
      setError(false);
    }, (e) => {
      console.error('進路活動の申請を読み込めませんでした', e);
      setLoading(false);
      setError(true);
    });
  }, [schoolId]);

  // 許可する(○) / 不許可にする(×、理由を書いてから)。押すとすぐ保存する
  const decide = async (r: CareerActivityRequest, approve: boolean, rejectedReason?: string) => {
    if (savingId || r.status !== 'pending') return;
    const decidedBy = getAuth().currentUser?.uid;
    if (!decidedBy) return;
    setSavingId(r.id);
    try {
      await updateDoc(doc(getFirestore(), 'careerActivityRequests', r.id), approve
        ? { status: 'approved', decidedAt: serverTimestamp(), decidedBy, updatedAt: serverTimestamp() }
        : { status: 'rejected', decidedAt: serverTimestamp(), decidedBy, updatedAt: serverTimestamp(), ...(rejectedReason ? { rejectedReason } : {}) });
    } catch (e) {
      console.error('進路活動の申請を更新できませんでした', e);
      window.alert('更新できませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setSavingId('');
    }
  };

  // 学生のクラス(学校と連携で登録した学科・学年・クラス)を名前の下に出す
  const classOf = useMemo(() => {
    const map = new Map<string, string>();
    allUsers.forEach((u) => {
      const e = u?.education ?? {};
      const label = [e.department, e.grade, e.class].filter((v) => typeof v === 'string' && v.trim()).join(' ');
      const uid = u?.id ?? u?.uid;
      if (uid) map.set(uid, label);
    });
    return map;
  }, [allUsers]);

  const allowedUids = useMemo(() => {
    if (hashTags.length === 0) return null;
    return new Set(filteredUsers.map((u) => u?.id ?? u?.uid).filter(Boolean));
  }, [hashTags, filteredUsers]);

  const inFilter = useMemo(
    () => items.filter((r) => !allowedUids || allowedUids.has(r.uid)),
    [items, allowedUids],
  );

  const rows = useMemo(() => {
    const t = normalize(text.trim());
    return inFilter
      .filter((r) => (tab === 'pending' ? r.status === 'pending' : r.status === 'approved' || r.status === 'logged'))
      .filter((r) => !t || normalize(`${r.studentName} ${r.companyName} ${r.storeName}`).includes(t))
      .sort((a, b) => (b.requestedAt?.getTime() ?? 0) - (a.requestedAt?.getTime() ?? 0));
  }, [inFilter, text, tab]);

  const pendingCount = useMemo(() => inFilter.filter((r) => r.status === 'pending').length, [inFilter]);
  const completedCount = useMemo(() => inFilter.filter((r) => r.status === 'approved' || r.status === 'logged').length, [inFilter]);

  return (
    <>
    <div className={Styles.toolbar}>
      <div className={Styles.tabs}>
        {showPendingTab && (
          <button
            type="button"
            className={`${Styles.tab} ${tab === 'pending' ? Styles.tabActive : ''}`}
            onClick={() => setTab('pending')}>
            承認待ち<span className={Styles.tabCount}>{pendingCount}</span>
          </button>
        )}
        <button
          type="button"
          className={`${Styles.tab} ${tab === 'completed' ? Styles.tabActive : ''}`}
          onClick={() => setTab('completed')}>
          実施ログ<span className={Styles.tabCount}>{completedCount}</span>
        </button>
      </div>
      <input
        className={Styles.search}
        placeholder="学生名・サロン名で検索"
        value={text}
        onChange={(e) => setText(e.target.value)} />
    </div>

    <div className={Styles.table}>
      <div className={Styles.nav}>
        <p className={Styles.colSubmitted}>申請日</p>
        <p className={Styles.colName}>学生名</p>
        <p className={Styles.colStore}>サロン名</p>
        <p className={Styles.colType}>活動種別</p>
        {tab === 'pending' ? (
          <>
            <p className={Styles.colIcon}>詳細</p>
            <p className={Styles.colApprove}>許可</p>
            <p className={Styles.colReject}>不許可</p>
          </>
        ) : (
          <>
            <p className={Styles.colStatus}>状態</p>
            <p className={Styles.colIcon}>詳細</p>
          </>
        )}
      </div>

      {loading ? (
        <p className={Styles.empty}>読み込み中...</p>
      ) : error ? (
        <p className={Styles.empty}>読み込めませんでした。時間をおいて、もう一度お試しください。</p>
      ) : rows.length === 0 ? (
        <p className={Styles.empty}>
          {text ? '該当する申請はありません。'
            : tab === 'pending' ? '承認待ちの申請はありません。'
            : 'まだ実施ログはありません。'}
        </p>
      ) : rows.map((r) => {
        const busy = savingId === r.id;
        return (
        <div key={r.id} className={Styles.row}>
          <p className={Styles.colSubmitted}>{formatShortDate(r.requestedAt)}</p>
          <div className={Styles.colName}>
            <p className={Styles.name}>{r.studentName || '(名前未登録)'}</p>
            {classOf.get(r.uid) && <p className={Styles.sub}>{classOf.get(r.uid)}</p>}
          </div>
          <div className={Styles.colStore}>
            <img
              className={Styles.logo}
              src={r.recruitId ? logoUrl(r.recruitId) : CompanyDefaultImage}
              alt=""
              onError={(e) => { e.currentTarget.src = CompanyDefaultImage; }}
            />
            <div className={Styles.storeText}>
              <p className={Styles.store}>{r.companyName || r.storeName}</p>
              {r.companyName && r.storeName && <p className={Styles.sub}>{r.storeName}</p>}
            </div>
          </div>
          <p className={Styles.colType}>{kindLabel(r)}</p>

          {tab === 'pending' ? (
            <>
              <div className={Styles.colIcon}>
                <button type="button" className={Styles.iconBtn} title="詳細を見る" aria-label="詳細を見る" onClick={() => setDetail(r)}>
                  <HiOutlineDocumentText />
                </button>
              </div>
              <div className={Styles.colApprove}>
                <button type="button" title="許可する" aria-label="許可する"
                  className={`${Styles.decideBtn} ${Styles.approveBtn}`}
                  disabled={busy}
                  onClick={() => decide(r, true)}>
                  <FaRegCircle />
                </button>
              </div>
              <div className={Styles.colReject}>
                <button type="button" title="不許可にする" aria-label="不許可にする"
                  className={`${Styles.decideBtn} ${Styles.rejectBtn}`}
                  disabled={busy}
                  onClick={() => setRejectTarget(r)}>
                  <RxCross2 className={Styles.crossIcon} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className={Styles.colStatus}>
                <span className={Styles[`status_${r.status}`]}>
                  {r.status === 'approved' ? '承認済み(事前承認校)' : '届出(自由)'}
                </span>
              </p>
              <div className={Styles.colIcon}>
                <button type="button" className={Styles.iconBtn} title="詳細を見る" aria-label="詳細を見る" onClick={() => setDetail(r)}>
                  <HiOutlineDocumentText />
                </button>
              </div>
            </>
          )}
        </div>
        );
      })}
    </div>

    {detail && <DetailDialog request={detail} className={classOf.get(detail.uid) ?? ''} onClose={() => setDetail(null)} />}
    {rejectTarget && (
      <RejectReasonDialog
        request={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={(reason) => {
          decide(rejectTarget, false, reason);
          setRejectTarget(null);
        }}
      />
    )}
    </>
  );
};

// ×(不許可にする)を押した直後に出す、理由を書くポップアップ。理由は任意。
// 書いた理由は学生アプリ側にメール・アプリ内通知で伝わる(functions/careerActivityApprovalDecision.js)。
const RejectReasonDialog: React.FC<{
  request: CareerActivityRequest;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}> = ({ request: r, onClose, onConfirm }) => {
  const [ reason, setReason ] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={Styles.modal} role="dialog" aria-modal="true" aria-labelledby="career-reject-reason-title">
      <div className={Styles.modal_bg} onClick={onClose} />
      <div className={`${Styles.modal_contents} ${Styles.periodModal}`}>
        <div className={Styles.modal_title}>
          <div>
            <h4 id="career-reject-reason-title">{r.studentName || '(名前未登録)'}を不許可にする</h4>
            <p className={Styles.modal_title_sub}>理由を書いてください(学生に伝わります。書かなくても不許可にできます)</p>
          </div>
          <RxCross2 className={Styles.modal_cross} onClick={onClose} aria-label="閉じる" />
        </div>

        <textarea
          className={Styles.reasonInput}
          placeholder="例: 日程が重複しているため"
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <div className={Styles.periodActions}>
          <button type="button" className={Styles.periodCancel} onClick={onClose}>キャンセル</button>
          <button type="button" className={Styles.periodReject} onClick={() => onConfirm(reason.trim())}>不許可にする</button>
        </div>
      </div>
    </div>
  );
};

const STATUS_LABELS: Record<Status, string> = {
  pending: '承認待ち',
  approved: '承認済み(事前承認校)',
  rejected: '不許可',
  logged: '届出(自由)',
};

const DetailDialog: React.FC<{ request: CareerActivityRequest; className: string; onClose: () => void }> = ({ request: r, className, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const section = (label: string, items: [string, React.ReactNode][]) => (
    <div className={Styles.modal_section}>
      <p className={Styles.modal_section_head}>{label}</p>
      {items.map(([ k, v ]) => (
        <div key={k} className={Styles.modal_item}>
          <p className={Styles.modal_item_label}>{k}</p>
          <div className={Styles.modal_item_value}>{v}</div>
        </div>
      ))}
    </div>
  );

  const progress: [string, React.ReactNode][] = [
    [ '状態', <span className={Styles[`status_${r.status}`]}>{STATUS_LABELS[r.status]}</span> ],
    [ '申請日', formatLongDate(r.requestedAt) ],
  ];
  if (r.status === 'approved' || r.status === 'rejected') {
    progress.push([ r.status === 'approved' ? '許可日' : '不許可日', formatLongDate(r.decidedAt) ]);
  }
  if (r.status === 'rejected' && r.rejectedReason) progress.push([ '不許可の理由', r.rejectedReason ]);

  return (
    <div className={Styles.modal} role="dialog" aria-modal="true" aria-labelledby="career-detail-title">
      <div className={Styles.modal_bg} onClick={onClose} />
      <div className={Styles.modal_contents}>
        <div className={Styles.modal_title}>
          <div>
            <h4 id="career-detail-title">{r.studentName || '(名前未登録)'}</h4>
            {className && <p className={Styles.modal_title_sub}>{className}</p>}
          </div>
          <RxCross2 className={Styles.modal_cross} onClick={onClose} aria-label="閉じる" />
        </div>

        {section('申請内容', [
          [ '活動種別', kindLabel(r) ],
        ])}

        {section('サロン', [
          [ 'サロン名', (
            <span className={Styles.modal_salon}>
              <img
                className={Styles.logo}
                src={r.recruitId ? logoUrl(r.recruitId) : CompanyDefaultImage}
                alt=""
                onError={(e) => { e.currentTarget.src = CompanyDefaultImage; }}
              />
              {r.companyName || '-'}
            </span>
          ) ],
          [ '店舗名', r.storeName || '-' ],
        ])}

        {section('状況', progress)}
      </div>
    </div>
  );
};

export default CareerActivityRequests;
